// src/pages/AreaMaster-page/AreaMasterPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdSearch,
  MdEdit,
  MdDelete,
  MdClear,
} from 'react-icons/md';
import {
  FaPlus,
  FaFileExcel,
  FaEye,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaChartArea,
  FaMapMarkerAlt,
  FaBuilding,
} from 'react-icons/fa';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#2563eb' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
  }),
};

const initialFormState = {
  areaname: '',
  areacode: '',
  stateid: '',
  pincode: '',
  hub: '',
  status: true,
};

const AreaMasterPage = () => {
  const [areas, setAreas] = useState([]);
  const [states, setStates] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [stateFilter, setStateFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('card');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [viewArea, setViewArea] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/area`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch areas');
      const { data } = await res.json();
      setAreas(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error loading areas');
      setAreas([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/state`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const { data } = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPincodes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/pincode`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const { data } = await res.json();
      setPincodes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchStates(), fetchPincodes(), fetchAreas()]);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, stateFilter]);

  // Options for react-select
  const stateOptions = useMemo(() => {
    return states.map((s) => ({ value: s._id, label: s.statename }));
  }, [states]);

  const pincodeOptions = useMemo(() => {
    return pincodes.map((p) => ({
      value: p._id,
      label: `${p.pincode} - ${p.locationname || 'N/A'}`,
    }));
  }, [pincodes]);

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!form.areaname?.trim()) errors.areaname = 'Area Name is required';
    if (!form.stateid) errors.stateid = 'State is required';
    if (!form.pincode) errors.pincode = 'Pincode is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenModal = (area = null) => {
    setFormErrors({});
    if (area) {
      setEditingArea(area);
      setForm({
        areaname: area.areaname || '',
        areacode: area.areacode || '',
        stateid: area.stateid?._id || area.stateid || '',
        pincode: area.pincode?._id || area.pincode || '',
        hub: area.hub || '',
        status: area.status !== false,
      });
    } else {
      setEditingArea(null);
      setForm(initialFormState);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingArea(null);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        areaname: form.areaname.trim(),
        areacode: form.areacode ? form.areacode.trim().toUpperCase() : '',
        stateid: form.stateid,
        pincode: form.pincode,
        hub: form.hub ? form.hub.trim().toUpperCase() : '',
        status: form.status,
      };

      const url = editingArea ? `${API_BASE_URL}/area/${editingArea._id}` : `${API_BASE_URL}/area`;
      const method = editingArea ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Operation failed');

      toast.success(editingArea ? 'Area updated successfully!' : 'Area created successfully!');
      handleCloseModal();
      fetchAreas();
    } catch (err) {
      toast.error(err.message || 'Error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'Delete Area?',
      text: `Are you sure you want to delete ${name || 'this area'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE_URL}/area/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to delete area');
        toast.success('Area deleted successfully');
        fetchAreas();
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  // Export Excel
  const handleExportExcel = () => {
    if (areas.length === 0) {
      toast.info('No area data to export');
      return;
    }
    const exportRows = filteredAreas.map((a, idx) => ({
      'S.No': idx + 1,
      'Area Name': a.areaname || '-',
      'Area Code': a.areacode || '-',
      State: a.stateid?.statename || states.find((s) => s._id === a.stateid)?.statename || '-',
      Pincode: a.pincode?.pincode || pincodes.find((p) => p._id === a.pincode)?.pincode || '-',
      Hub: a.hub || '-',
      Status: a.status !== false ? 'Active' : 'Inactive',
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Areas');
    XLSX.writeFile(workbook, `Area_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Areas exported to Excel successfully!');
  };

  // Filter Logic
  const filteredAreas = useMemo(() => {
    return areas.filter((a) => {
      const isActive = a.status !== false;
      if (statusFilter === 'ACTIVE' && !isActive) return false;
      if (statusFilter === 'INACTIVE' && isActive) return false;

      if (stateFilter !== 'ALL') {
        const sId = a.stateid?._id || a.stateid;
        if (sId !== stateFilter) return false;
      }

      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();
      const stateName = a.stateid?.statename || states.find((s) => s._id === a.stateid)?.statename || '';
      const pin = a.pincode?.pincode || pincodes.find((p) => p._id === a.pincode)?.pincode || '';

      return (
        (a.areaname || '').toLowerCase().includes(term) ||
        (a.areacode || '').toLowerCase().includes(term) ||
        (a.hub || '').toLowerCase().includes(term) ||
        stateName.toLowerCase().includes(term) ||
        pin.includes(term)
      );
    });
  }, [areas, searchTerm, statusFilter, stateFilter, states, pincodes]);

  const totalItems = filteredAreas.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAreas.slice(start, start + pageSize);
  }, [filteredAreas, currentPage, pageSize]);

  const totalActive = useMemo(() => areas.filter((a) => a.status !== false).length, [areas]);
  const totalInactive = areas.length - totalActive;

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header & KPI Summary */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            Area Master
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage localities, regional zones, operational hubs & mapped pincodes
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition shadow-sm"
          >
            <FaFileExcel className="text-green-600 text-base" />
            <span>Export Excel</span>
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition shadow-md hover:shadow-lg"
          >
            <FaPlus className="text-sm" />
            <span>Add New Area</span>
          </button>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Total Areas</p>
            <p className="text-2xl font-bold text-gray-800">{areas.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <FaChartArea />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Active</p>
            <p className="text-2xl font-bold text-green-600">{totalActive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-bold">
            <FaCheckCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Inactive</p>
            <p className="text-2xl font-bold text-gray-500">{totalInactive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center font-bold">
            <FaTimesCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">States Mapped</p>
            <p className="text-2xl font-bold text-indigo-600">{states.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
            <FaBuilding />
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6 mb-8">
        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="relative flex-1 min-w-[260px]">
            <MdSearch className="absolute left-3.5 top-3 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by area name, code, hub, state, pincode..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-3 text-gray-400">
                <MdClear className="text-lg" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            {/* State Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">State:</span>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 max-w-[160px]"
              >
                <option value="ALL">All States</option>
                {states.map((s) => (
                  <option key={s._id} value={s._id}>{s.statename}</option>
                ))}
              </select>
            </div>

            {(searchTerm || statusFilter !== 'ALL' || stateFilter !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setStateFilter('ALL');
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 underline"
              >
                Reset
              </button>
            )}

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200 ml-auto lg:ml-2">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'card' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewGrid className="text-base" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'table' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewList className="text-base" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-20 text-center text-gray-400 flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm font-semibold">Loading areas...</p>
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="py-16 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FaChartArea className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-base font-bold text-gray-700">No areas found</p>
          </div>
        ) : viewMode === 'card' ? (
          /* Landscape Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
            {paginatedData.map((a, idx) => {
              const isActive = a.status !== false;
              const sNo = (currentPage - 1) * pageSize + idx + 1;
              const stateName = a.stateid?.statename || states.find((s) => s._id === a.stateid)?.statename || '-';
              const pinCode = a.pincode?.pincode || pincodes.find((p) => p._id === a.pincode)?.pincode || '-';

              return (
                <div
                  key={a._id || idx}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-blue-400 p-5 flex flex-col justify-between transition-all"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-base flex items-center justify-center shadow-sm">
                          {a.areaname?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-gray-900 text-base leading-tight">
                              {a.areaname}
                            </h3>
                            <span className="text-xs text-gray-400 font-medium">#{sNo}</span>
                          </div>
                          {a.areacode && (
                            <p className="text-xs font-mono font-semibold text-blue-600 mt-0.5">
                              Code: {a.areacode}
                            </p>
                          )}
                        </div>
                      </div>

                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                          isActive
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 my-3.5 bg-gray-50/70 p-3 rounded-lg border border-gray-100 text-xs">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">State</span>
                        <span className="font-semibold text-gray-800 truncate block">{stateName}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Pincode</span>
                        <span className="font-mono font-bold text-blue-600 truncate block">{pinCode}</span>
                      </div>
                    </div>

                    {a.hub && (
                      <div className="text-xs text-gray-600 mb-2">
                        Hub: <strong className="text-gray-800">{a.hub}</strong>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                    <button
                      onClick={() => setViewArea(a)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                    >
                      <FaEye /> <span>View</span>
                    </button>
                    <button
                      onClick={() => handleOpenModal(a)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
                    >
                      <MdEdit /> <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(a._id, a.areaname)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    >
                      <MdDelete /> <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table List View */
          <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
            <table className="w-full text-left text-sm text-gray-600 border-collapse">
              <thead className="bg-blue-600 text-white text-xs uppercase font-semibold">
                <tr>
                  <th className="py-3 px-4 text-center w-14">S.No</th>
                  <th className="py-3 px-4">Area Name</th>
                  <th className="py-3 px-4">Area Code</th>
                  <th className="py-3 px-4">State</th>
                  <th className="py-3 px-4">Pincode</th>
                  <th className="py-3 px-4">Hub</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedData.map((a, idx) => {
                  const isActive = a.status !== false;
                  const sNo = (currentPage - 1) * pageSize + idx + 1;
                  const stateName = a.stateid?.statename || states.find((s) => s._id === a.stateid)?.statename || '-';
                  const pinCode = a.pincode?.pincode || pincodes.find((p) => p._id === a.pincode)?.pincode || '-';

                  return (
                    <tr key={a._id || idx} className="hover:bg-blue-50/50 transition">
                      <td className="py-3.5 px-4 text-center text-gray-500 font-semibold">{sNo}</td>
                      <td className="py-3.5 px-4 font-bold text-gray-900">{a.areaname}</td>
                      <td className="py-3.5 px-4 font-mono font-semibold text-blue-600">{a.areacode || '-'}</td>
                      <td className="py-3.5 px-4 font-medium text-gray-700">{stateName}</td>
                      <td className="py-3.5 px-4 font-mono font-bold text-gray-800">{pinCode}</td>
                      <td className="py-3.5 px-4 text-gray-700">{a.hub || '-'}</td>
                      <td className="py-3.5 px-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center items-center gap-1.5">
                          <button onClick={() => setViewArea(a)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <FaEye />
                          </button>
                          <button onClick={() => handleOpenModal(a)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded">
                            <MdEdit />
                          </button>
                          <button onClick={() => handleDelete(a._id, a.areaname)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 outline-none font-semibold"
              >
                {[6, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>entries per page</span>
              <span className="hidden md:inline ml-3 text-gray-400">
                (Page {currentPage} of {totalPages})
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((pageNum, idx, arr) => (
                  <React.Fragment key={pageNum}>
                    {idx > 0 && pageNum - arr[idx - 1] > 1 && <span className="px-1 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                        currentPage === pageNum ? 'bg-blue-600 text-white' : 'border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Add/Edit Area */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaChartArea className="text-xl" />
                <h3 className="text-xl font-bold">{editingArea ? 'Edit Area' : 'Add New Area'}</h3>
              </div>
              <button onClick={handleCloseModal} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Area Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. T. Nagar"
                    value={form.areaname}
                    onChange={(e) => setForm({ ...form, areaname: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.areaname ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.areaname && <p className="text-red-500 text-xs mt-1">{formErrors.areaname}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Area Code (Optional)
                  </label>
                  <input
                    type="text"
                    maxLength={10}
                    placeholder="e.g. TN-01"
                    value={form.areacode}
                    onChange={(e) => setForm({ ...form, areacode: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase font-mono outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Searchable State */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={stateOptions}
                    value={stateOptions.find((s) => s.value === form.stateid) || null}
                    onChange={(opt) => setForm({ ...form, stateid: opt ? opt.value : '' })}
                    placeholder="Search / select state..."
                    styles={customSelectStyles}
                  />
                  {formErrors.stateid && <p className="text-red-500 text-xs mt-1">{formErrors.stateid}</p>}
                </div>

                {/* Searchable Pincode */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={pincodeOptions}
                    value={pincodeOptions.find((p) => p.value === form.pincode) || null}
                    onChange={(opt) => setForm({ ...form, pincode: opt ? opt.value : '' })}
                    placeholder="Search / select pincode..."
                    styles={customSelectStyles}
                  />
                  {formErrors.pincode && <p className="text-red-500 text-xs mt-1">{formErrors.pincode}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Hub (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. MAA"
                    value={form.hub}
                    onChange={(e) => setForm({ ...form, hub: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase font-mono outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="area-status"
                    checked={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="area-status" className="text-xs font-bold text-gray-700 cursor-pointer">
                    Area Enabled (Active)
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-7 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow transition flex items-center gap-2"
                >
                  {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <span>{editingArea ? 'Update Area' : 'Create Area'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: View Details */}
      {viewArea && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <h3 className="text-lg font-bold">{viewArea.areaname}</h3>
              <button onClick={() => setViewArea(null)} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Area Code</span>
                  <strong className="font-mono text-blue-600 text-base">{viewArea.areacode || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">State</span>
                  <strong className="text-gray-800 text-base">{viewArea.stateid?.statename || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Pincode</span>
                  <strong className="font-mono text-gray-800 text-base">{viewArea.pincode?.pincode || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Hub</span>
                  <strong className="text-gray-800">{viewArea.hub || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Status</span>
                  <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-bold ${viewArea.status !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {viewArea.status !== false ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
              <button onClick={() => setViewArea(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                Close
              </button>
              <button
                onClick={() => {
                  const target = viewArea;
                  setViewArea(null);
                  handleOpenModal(target);
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaMasterPage;